/**
 * ReplayScene.ts — Interactive Phaser scene for watching match replays
 *
 * Renders graph canvas, animates projectile curves for past turns,
 * and manages interactive step forward, rewind, jump, and auto-play logic.
 */

import * as Phaser from 'phaser';
import { ReplayData, exportReplayCode } from '../core/replay';
import { CoordMapper, createCoordMapper, computeTrajectory, checkHit, DEFAULT_FIELD_BOUNDS } from '../core/trajectory';
import { parseFunction } from '../core/parser';
import { ReplayUI, ReplayLogItem } from '../ui/ReplayUI';
import { Obstacle, isPointInObstacle, checkObstacleCollision, applyBite } from '../core/obstacles';
import { playShootSound, playExplosionSound, playDeathSound } from '../core/audio';

export class ReplayScene extends Phaser.Scene {
  private replayUI: ReplayUI;
  private replayData!: ReplayData;

  private currentStep: number = 0;
  private isAutoPlaying: boolean = false;
  private autoPlayTimer: Phaser.Time.TimerEvent | null = null;

  // Graphics layers
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private obstacleGraphics!: Phaser.GameObjects.Graphics;
  private trajectoryGraphics!: Phaser.GameObjects.Graphics;
  private soldierGraphics!: Phaser.GameObjects.Graphics;

  // Coordinate mapper
  private mapper!: CoordMapper;

  // Colors
  private readonly TEAM_COLORS = [0x1D4E89, 0xF76707]; // team1, team2 from spec

  constructor() {
    super({ key: 'ReplayScene' });
    this.replayUI = new ReplayUI();
  }

  init(data: { replayData: ReplayData }): void {
    if (!data.replayData) {
      this.scene.start('MenuScene');
      return;
    }
    this.replayData = data.replayData;
    this.currentStep = 0;
    this.isAutoPlaying = false;
  }

  create(): void {
    const { width, height } = this.scale;
    this.mapper = createCoordMapper(width, height);

    // Setup graphics layers (grid -> obstacles -> trajectory -> soldiers)
    this.gridGraphics = this.add.graphics();
    this.obstacleGraphics = this.add.graphics();
    this.trajectoryGraphics = this.add.graphics();
    this.soldierGraphics = this.add.graphics();

    this.drawDesmosGrid();
    this.goToStep(0);

    // Prepare log items for ReplayUI
    const logs: ReplayLogItem[] = this.replayData.turns.map((t, i) => ({
      stepIndex: i + 1,
      teamIndex: t.teamIndex,
      expression: t.expression,
      hits: t.hits,
    }));

    // Initialize ReplayUI overlay
    this.replayUI.show(
      {
        onFirst: () => this.goToStep(0),
        onPrev: () => this.goToStep(Math.max(0, this.currentStep - 1)),
        onNext: () => this.nextStep(),
        onLast: () => this.goToStep(this.replayData.turns.length),
        onToggleAutoPlay: () => this.toggleAutoPlay(),
        onJumpToStep: (step) => this.goToStep(step),
        onCopyCode: () => {
          const code = exportReplayCode(this.replayData);
          if (navigator.clipboard) {
            navigator.clipboard.writeText(code);
            this.replayUI.showToast('Replay code copied to clipboard!');
          } else {
            this.replayUI.showToast('Code: ' + code);
          }
        },
        onBackToMenu: () => {
          this.stopAutoPlay();
          this.replayUI.destroy();
          this.scene.start('MenuScene');
        },
        getChatLog: () => this.replayData.chatLog || [],
      },
      this.replayData.turns.length,
      logs,
    );

    // Resize handler
    this.scale.on('resize', this.handleResize, this);
  }

  private handleResize(gameSize: Phaser.Structs.Size): void {
    this.mapper = createCoordMapper(gameSize.width, gameSize.height);
    this.drawDesmosGrid();
    this.drawSoldiersAtStep(this.currentStep);
    this.redrawPastTrajectoriesUpToStep(this.currentStep);
    this.drawObstaclesAtStep(this.currentStep);
  }

  private drawGridLines(g: Phaser.GameObjects.Graphics): void {
    const m = this.mapper;
    const bounds = m.bounds;

    const majorColor = 0xd8d5ce;
    const minorColor = 0xeae8e3;
    const axisColor = 0xb4b2a9;

    g.lineStyle(1, majorColor, 1);
    g.beginPath();
    const startXMajor = Math.ceil(bounds.minX / 5) * 5;
    for (let x = startXMajor; x <= bounds.maxX; x += 5) {
      const sx = m.toScreenX(x);
      g.moveTo(sx, m.toScreenY(bounds.maxY));
      g.lineTo(sx, m.toScreenY(bounds.minY));
    }
    const startYMajor = Math.ceil(bounds.minY / 5) * 5;
    for (let y = startYMajor; y <= bounds.maxY; y += 5) {
      const sy = m.toScreenY(y);
      g.moveTo(m.toScreenX(bounds.minX), sy);
      g.lineTo(m.toScreenX(bounds.maxX), sy);
    }
    g.strokePath();

    g.lineStyle(1, minorColor, 1);
    g.beginPath();
    const startXMinor = Math.ceil(bounds.minX);
    for (let x = startXMinor; x <= bounds.maxX; x += 1) {
      if (x % 5 === 0) continue;
      const sx = m.toScreenX(x);
      g.moveTo(sx, m.toScreenY(bounds.maxY));
      g.lineTo(sx, m.toScreenY(bounds.minY));
    }
    const startYMinor = Math.ceil(bounds.minY);
    for (let y = startYMinor; y <= bounds.maxY; y += 1) {
      if (y % 5 === 0) continue;
      const sy = m.toScreenY(y);
      g.moveTo(m.toScreenX(bounds.minX), sy);
      g.lineTo(m.toScreenX(bounds.maxX), sy);
    }
    g.strokePath();

    g.lineStyle(2, axisColor, 1);
    g.beginPath();
    const yZero = m.toScreenY(0);
    g.moveTo(m.toScreenX(bounds.minX), yZero);
    g.lineTo(m.toScreenX(bounds.maxX), yZero);
    const xZero = m.toScreenX(0);
    g.moveTo(xZero, m.toScreenY(bounds.maxY));
    g.lineTo(xZero, m.toScreenY(bounds.minY));
    g.strokePath();
  }

  private drawDesmosGrid(): void {
    const g = this.gridGraphics;
    g.clear();
    this.drawGridLines(g);
  }

  /**
   * Evaluates soldier health and positions up to stepIndex based on recorded turn hits.
   */
  private getSoldiersStateAtStep(stepIndex: number) {
    // Clone initial soldiers with safe teamIndex fallback
    const soldiers = this.replayData.initialSoldiers.map(s => ({
      ...s,
      teamIndex: typeof s.teamIndex === 'number' ? s.teamIndex : (s.x < 0 ? 0 : 1),
      alive: true,
    }));

    // Apply turns up to stepIndex
    for (let i = 0; i < stepIndex; i++) {
      const turn = this.replayData.turns[i];
      if (!turn) break;

      const turnTeamIdx = typeof turn.teamIndex === 'number' ? turn.teamIndex : 0;
      const opposingTeamIdx = turnTeamIdx === 0 ? 1 : 0;

      // If turn recorded hits > 0, eliminate enemy targets
      if (turn.hits && turn.hits > 0) {
        const aliveEnemies = soldiers.filter(s => s.teamIndex === opposingTeamIdx && s.alive);
        for (let k = 0; k < Math.min(turn.hits, aliveEnemies.length); k++) {
          aliveEnemies[k].alive = false;
        }
      }
    }

    return soldiers;
  }

  // Soldier label text objects
  private soldierTextLabels: Phaser.GameObjects.Text[] = [];

  /**
   * Draws soldiers on canvas at target step index.
   */
  private drawSoldiersAtStep(stepIndex: number): void {
    const g = this.soldierGraphics;
    g.clear();

    // Destroy existing text labels
    for (const label of this.soldierTextLabels) {
      label.destroy();
    }
    this.soldierTextLabels = [];

    const soldiers = this.getSoldiersStateAtStep(stepIndex);
    const m = this.mapper;

    const currentTurn = this.replayData.turns[stepIndex];
    let activeShooter: any = null;
    if (currentTurn) {
      const turnTeamIdx = typeof currentTurn.teamIndex === 'number' ? currentTurn.teamIndex : 0;
      const teamSoldiers = soldiers.filter(s => (s.teamIndex ?? (s.x < 0 ? 0 : 1)) === turnTeamIdx);
      activeShooter = teamSoldiers[currentTurn.soldierIndex ?? 0] || teamSoldiers.find(s => s.alive) || teamSoldiers[0];
    }

    const TEAM_HEX = ['#1D4E89', '#d94040'];

    for (const soldier of soldiers) {
      if (!soldier.alive) continue;

      const teamIdx = typeof soldier.teamIndex === 'number' && soldier.teamIndex >= 0
        ? soldier.teamIndex
        : (soldier.x < 0 ? 0 : 1);

      const sx = m.toScreenX(soldier.x);
      const sy = m.toScreenY(soldier.y);
      const color = this.TEAM_COLORS[teamIdx] ?? (teamIdx === 0 ? 0x1D4E89 : 0xd94040);
      const colorHex = TEAM_HEX[teamIdx] ?? (teamIdx === 0 ? '#1D4E89' : '#d94040');
      const isTeam1 = teamIdx === 0;

      const isCurrentShooter = activeShooter && activeShooter.x === soldier.x && activeShooter.y === soldier.y;

      // Calculate exact visual radius to match the physics hitRadius used during the match (0.5)
      const hitRadius = 0.5;
      const SOLDIER_RADIUS = m.toScreenX(hitRadius) - m.toScreenX(0);

      // Solid inner fill
      g.fillStyle(color, 0.9);
      g.fillCircle(sx, sy, SOLDIER_RADIUS);

      // Outer ring
      g.lineStyle(2, 0xffffff, 0.9);
      g.strokeCircle(sx, sy, SOLDIER_RADIUS);

      // Coordinate label below soldier
      if (this.replayData.showCoordinates) {
        const posText = `(${soldier.x.toFixed(1)}, ${soldier.y.toFixed(1)})`;
        const posLabel = this.add.text(sx, sy + 14, posText, {
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: '10px',
          color: '#6e6e7e',
        }).setOrigin(0.5, 0);
        this.soldierTextLabels.push(posLabel);
      }

      // Badge label above soldier: "YOU" or "BOT"
      const badgeText = isTeam1 ? 'YOU' : 'BOT';
      const arrowY = sy - 14;

      if (isCurrentShooter) {
        // Active shooter indicator: downward triangle arrow
        const arrowSize = 5;
        g.fillStyle(color, 1.0);
        g.beginPath();
        g.moveTo(sx, arrowY);
        g.lineTo(sx - arrowSize, arrowY - arrowSize * 1.6);
        g.lineTo(sx + arrowSize, arrowY - arrowSize * 1.6);
        g.closePath();
        g.fillPath();

        const badgeLabel = this.add.text(sx, arrowY - arrowSize * 1.6 - 2, badgeText, {
          fontFamily: 'Space Grotesk, sans-serif',
          fontSize: '11px',
          fontStyle: 'bold',
          color: colorHex,
        }).setOrigin(0.5, 1);
        this.soldierTextLabels.push(badgeLabel);
      } else {
        const badgeLabel = this.add.text(sx, arrowY - 2, badgeText, {
          fontFamily: 'Space Grotesk, sans-serif',
          fontSize: '10px',
          fontStyle: 'bold',
          color: colorHex,
        }).setOrigin(0.5, 1);
        this.soldierTextLabels.push(badgeLabel);
      }
    }
  }

  /**
   * Redraws all trajectories fired up to stepIndex.
   */
  private redrawPastTrajectoriesUpToStep(stepIndex: number): void {
    const g = this.trajectoryGraphics;
    g.clear();
    const m = this.mapper;

    for (let i = 0; i < stepIndex; i++) {
      const turn = this.replayData.turns[i];
      if (!turn) break;

      const turnTeamIdx = typeof turn.teamIndex === 'number' ? turn.teamIndex : 0;
      const soldiersBeforeTurn = this.getSoldiersStateAtStep(i);
      const teamSoldiers = soldiersBeforeTurn.filter(s => (s.teamIndex ?? (s.x < 0 ? 0 : 1)) === turnTeamIdx);
      const shooter = teamSoldiers[turn.soldierIndex ?? 0] || teamSoldiers.find(s => s.alive) || teamSoldiers[0];
      if (!shooter) continue;

      const parseResult = parseFunction(turn.expression);
      if (!parseResult.ok) continue;

      const color = this.TEAM_COLORS[turnTeamIdx] ?? (turnTeamIdx === 0 ? 0x1D4E89 : 0xd94040);
      const opposingSoldiers = soldiersBeforeTurn.filter(s => (s.teamIndex ?? (s.x < 0 ? 0 : 1)) !== turnTeamIdx && s.alive);
      let direction: 1 | -1 = turnTeamIdx === 0 ? 1 : -1;
      if (opposingSoldiers.length > 0) {
        direction = opposingSoldiers[0].x >= shooter.x ? 1 : -1;
      }

      let points = computeTrajectory(parseResult.evaluate, shooter.x, shooter.y, direction, m.bounds);

      // Truncate trajectory at obstacle boundary
      const obstaclesAtTurn = this.getObstaclesStateAtStep(i);
      const obsHit = checkObstacleCollision(points, obstaclesAtTurn);
      if (obsHit) {
        points = points.slice(0, obsHit.hitIndex + 1);
      }

      if (points.length === 0) continue;

      g.lineStyle(2, color, 0.6);
      g.beginPath();
      g.moveTo(m.toScreenX(points[0].x), m.toScreenY(points[0].y));

      for (let p = 1; p < points.length; p++) {
        g.lineTo(m.toScreenX(points[p].x), m.toScreenY(points[p].y));
      }

      g.strokePath();
    }
  }

  /**
   * Evaluates obstacle bites accumulated up to stepIndex.
   */
  private getObstaclesStateAtStep(stepIndex: number): Obstacle[] {
    if (!this.replayData.initialObstacles) return [];

    // Deep clone initial obstacles
    const obstacles: Obstacle[] = this.replayData.initialObstacles.map(obs => ({
      ...obs,
      bites: [...(obs.bites || [])],
    }));

    const soldiers = this.replayData.initialSoldiers.map(s => ({
      ...s,
      teamIndex: typeof s.teamIndex === 'number' ? s.teamIndex : (s.x < 0 ? 0 : 1),
      alive: true,
    }));

    // Apply turns up to stepIndex to calculate obstacle bite deformations
    for (let i = 0; i < stepIndex; i++) {
      const turn = this.replayData.turns[i];
      if (!turn) break;

      const turnTeamIdx = typeof turn.teamIndex === 'number' ? turn.teamIndex : 0;
      const teamSoldiers = soldiers.filter(s => (s.teamIndex ?? (s.x < 0 ? 0 : 1)) === turnTeamIdx);
      const shooter = teamSoldiers[turn.soldierIndex ?? 0] || teamSoldiers.find(s => s.alive) || teamSoldiers[0];
      if (!shooter) continue;

      const parseResult = parseFunction(turn.expression);
      if (!parseResult.ok) continue;

      const opposingSoldiers = soldiers.filter(s => (s.teamIndex ?? (s.x < 0 ? 0 : 1)) !== turnTeamIdx && s.alive);
      let direction: 1 | -1 = turnTeamIdx === 0 ? 1 : -1;
      if (opposingSoldiers.length > 0) {
        direction = opposingSoldiers[0].x >= shooter.x ? 1 : -1;
      }

      const trajectory = computeTrajectory(parseResult.evaluate, shooter.x, shooter.y, direction, this.mapper.bounds);

      const hit = checkObstacleCollision(trajectory, obstacles);
      if (hit) {
        applyBite(hit.obstacle, hit.hitPoint.x, hit.hitPoint.y, 0.65);
      }
    }

    return obstacles;
  }

  private drawObstaclesAtStep(stepIndex: number): void {
    if (!this.obstacleGraphics) return;
    this.obstacleGraphics.clear();

    const obstacles = this.getObstaclesStateAtStep(stepIndex);
    const m = this.mapper;
    
    const majorColor = 0xd8d5ce;
    const minorColor = 0xeae8e3;
    const axisColor = 0xb4b2a9;

    for (const obs of obstacles) {
      const sx = m.toScreenX(obs.x);
      const sy = m.toScreenY(obs.y);
      const sRadius = Math.abs(m.toScreenX(obs.x + obs.radius) - sx);

      const obsColor = 0x2B2A28; 
      const obsStroke = 0x2B2A28;

      this.obstacleGraphics.fillStyle(obsColor, 1.0);
      this.obstacleGraphics.fillCircle(sx, sy, sRadius);
      this.obstacleGraphics.lineStyle(2, obsStroke, 1.0);
      this.obstacleGraphics.strokeCircle(sx, sy, sRadius);

      const bgColor = 0xF8F7F4;

      for (const bite of obs.bites) {
        const bx = m.toScreenX(bite.x);
        const by = m.toScreenY(bite.y);
        const bRadius = Math.abs(m.toScreenX(bite.x + bite.radius) - bx);

        this.obstacleGraphics.fillStyle(bgColor, 1.0);
        this.obstacleGraphics.fillCircle(bx, by, bRadius);

        // Mathematical Grid Clipping
        // Draw the exact grid line segments that fall within this bite circle
        const drawVertLine = (x: number, color: number, width: number) => {
            if (Math.abs(x - bite.x) < bite.radius) {
                const dy = Math.sqrt(bite.radius * bite.radius - (x - bite.x) ** 2);
                const sy1 = m.toScreenY(bite.y - dy);
                const sy2 = m.toScreenY(bite.y + dy);
                const px = m.toScreenX(x);
                this.obstacleGraphics.lineStyle(width, color, 1);
                this.obstacleGraphics.beginPath();
                this.obstacleGraphics.moveTo(px, sy1);
                this.obstacleGraphics.lineTo(px, sy2);
                this.obstacleGraphics.strokePath();
            }
        };

        const drawHorizLine = (y: number, color: number, width: number) => {
            if (Math.abs(y - bite.y) < bite.radius) {
                const dx = Math.sqrt(bite.radius * bite.radius - (y - bite.y) ** 2);
                const sx1 = m.toScreenX(bite.x - dx);
                const sx2 = m.toScreenX(bite.x + dx);
                const py = m.toScreenY(y);
                this.obstacleGraphics.lineStyle(width, color, 1);
                this.obstacleGraphics.beginPath();
                this.obstacleGraphics.moveTo(sx1, py);
                this.obstacleGraphics.lineTo(sx2, py);
                this.obstacleGraphics.strokePath();
            }
        };

        const startX = Math.ceil(bite.x - bite.radius);
        const endX = Math.floor(bite.x + bite.radius);
        for (let x = startX; x <= endX; x++) {
            if (x === 0) {
                drawVertLine(x, axisColor, 2);
            } else if (x % 5 === 0) {
                drawVertLine(x, majorColor, 1);
            } else {
                drawVertLine(x, minorColor, 1);
            }
        }

        const startY = Math.ceil(bite.y - bite.radius);
        const endY = Math.floor(bite.y + bite.radius);
        for (let y = startY; y <= endY; y++) {
            if (y === 0) {
                drawHorizLine(y, axisColor, 2);
            } else if (y % 5 === 0) {
                drawHorizLine(y, majorColor, 1);
            } else {
                drawHorizLine(y, minorColor, 1);
            }
        }
      }
    }
  }

  /**
   * Jumps directly to a given step index.
   */
  private goToStep(stepIndex: number): void {
    this.currentStep = Math.max(0, Math.min(this.replayData.turns.length, stepIndex));
    this.drawObstaclesAtStep(this.currentStep);
    this.drawSoldiersAtStep(this.currentStep);
    this.redrawPastTrajectoriesUpToStep(this.currentStep);
    this.replayUI.updateProgress(this.currentStep, this.replayData.turns.length, this.isAutoPlaying);
  }

  /**
   * Steps forward to next turn.
   */
  private nextStep(): void {
    if (this.currentStep >= this.replayData.turns.length) {
      this.stopAutoPlay();
      return;
    }

    const turnIndex = this.currentStep;
    this.goToStep(this.currentStep + 1);

    // Play sounds
    playShootSound();
    
    const turn = this.replayData.turns[turnIndex];
    if (turn && turn.hits > 0) {
      setTimeout(() => {
        playExplosionSound();
        playDeathSound();
      }, 300); // Delay explosion slightly since there's no animation
    }
  }

  /**
   * Toggles auto-play interval timer.
   */
  private toggleAutoPlay(): boolean {
    if (this.isAutoPlaying) {
      this.stopAutoPlay();
      return false;
    } else {
      if (this.currentStep >= this.replayData.turns.length) {
        this.goToStep(0);
      }

      this.isAutoPlaying = true;
      this.autoPlayTimer = this.time.addEvent({
        delay: 1400,
        loop: true,
        callback: () => {
          if (this.currentStep >= this.replayData.turns.length) {
            this.stopAutoPlay();
          } else {
            this.nextStep();
          }
        },
      });

      return true;
    }
  }

  private stopAutoPlay(): void {
    this.isAutoPlaying = false;
    if (this.autoPlayTimer) {
      this.autoPlayTimer.destroy();
      this.autoPlayTimer = null;
    }
    this.replayUI.updateProgress(this.currentStep, this.replayData.turns.length, false);
  }
}
